import { useMemo } from "react";
import { ServerInferResponseBody } from "@ts-rest/core";
import { VStack } from "@design-system/VStack/VStack";
import { documentCategoryContract } from "@common/contracts/document-category.contract";
import { DocumentCategory } from "@common/types/documentCategory";
import { getMainDocumentTaxonomy } from "@common/documentCategories";
import { pluralize } from "@common/helpers/string";
import { useFlatDocumentCategories } from "@api/documentCategory.api";
import { Segment } from "@helpers/segment";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import styles from "./DocumentCategoryStats.module.scss";
import { documentStatusGradientColors } from "./DocumentCategoryStatColors";

interface CategoryEntry {
    id: string;
}

type DocumentCategoryStat = ServerInferResponseBody<
    typeof documentCategoryContract.getDocumentCategoryStatistics,
    200
>[number];

interface Props {
    statsMap: Map<string, DocumentCategoryStat>;
    total: number;
    categories: CategoryEntry[];
    toggleCategory: (categoryId: string) => void;
}

const DocumentCategoryStatTooltip = ({
    category,
    valueOverride,
    subtitle,
}: {
    category: DocumentCategory;
    valueOverride: string;
    subtitle: React.ReactNode;
}) => (
    <VStack className={styles.categoryTooltip}>
        <span className={styles.headline}>{category.label}</span>
        {!category.parentCategoryId && (
            <span className={styles.subHeadline}>(Main Category)</span>
        )}
        <span className={styles.value}>{valueOverride}</span>
        <span className={styles.subValue}>{subtitle}</span>
    </VStack>
);

const getValueOverride = (
    statsMap: Map<string, DocumentCategoryStat>,
    category: DocumentCategory,
    total: number,
): string =>
    `${(((statsMap.get(category.id)?.count ?? 0) / total) * 100).toFixed(2)}%`;

const getSubtitle = (
    statsMap: Map<string, DocumentCategoryStat>,
    category: DocumentCategory,
): string =>
    `${statsMap.get(category.id)?.count ?? 0} ${pluralize("document", statsMap.get(category.id)?.count ?? 0)}`;

export const DocumentCategoriesStats: React.FC<Props> = ({
    statsMap,
    total,
    categories,
    toggleCategory,
}) => {
    const categoriesTree = useFlatDocumentCategories();

    const segments = useMemo(() => {
        if (!total) {
            return [];
        }

        return categoriesTree
            .filter((category) => !category.parentCategoryId)
            .map((category) => {
                const groupTotal =
                    (statsMap.get(category.id)?.count ?? 0) +
                    (category.subcategories ?? []).reduce(
                        (acc, subcategory) =>
                            acc + (statsMap.get(subcategory.id)?.count ?? 0),
                        0,
                    );

                if (groupTotal === 0) {
                    return;
                }

                const isHidden = !categories.some(
                    ({ id }) => id === category.id,
                );

                const segment: Segment & {
                    isHidden?: boolean;
                } = {
                    id: category.id,
                    label: category.label,
                    value: isHidden
                        ? 0
                        : (statsMap.get(category.id)?.count ?? 0),
                    valueOverride: getValueOverride(statsMap, category, total),
                    subtitle: getSubtitle(statsMap, category),
                    colors: documentStatusGradientColors[
                        getMainDocumentTaxonomy(category.documentTaxonomy)
                    ],
                    isHidden,
                    subsegments: (category.subcategories ?? [])
                        .filter(
                            (subcategory) =>
                                categories.some(
                                    // eslint-disable-next-line sonarjs/no-nested-functions
                                    ({ id }) => id === subcategory.id,
                                ) &&
                                (statsMap.get(subcategory.id)?.count ?? 0) > 0,
                        )
                        .map((subcategory) => ({
                            id: subcategory.id,
                            label: category.label,
                            value: statsMap.get(subcategory.id)?.count ?? 0,
                            valueOverride: getValueOverride(
                                statsMap,
                                category,
                                total,
                            ),
                            subtitle: getSubtitle(statsMap, category),
                            tooltip: (
                                <DocumentCategoryStatTooltip
                                    category={subcategory}
                                    valueOverride={getValueOverride(
                                        statsMap,
                                        subcategory,
                                        total,
                                    )}
                                    subtitle={getSubtitle(
                                        statsMap,
                                        subcategory,
                                    )}
                                />
                            ),
                        })),
                };

                segment.tooltip = (
                    <DocumentCategoryStatTooltip
                        category={category}
                        valueOverride={segment.valueOverride ?? ""}
                        subtitle={segment.subtitle}
                    />
                );

                // If there are subsegments, we want to override the main segments display info
                // with the overall stats for the main category
                if (segment.subsegments && segment.subsegments.length > 0) {
                    if (segment.value > 0) {
                        // adding the main category as the first subsegment
                        segment.subsegments.unshift({
                            ...segment,
                        });
                        // the segment as first subsegment's subsegments must be set to undefined, otherwise,
                        // the subsegment widths are incorrectly calcualted
                        (segment.subsegments[0] as Segment).subsegments =
                            undefined;
                    }
                    const segmentTotalWithSubsegments =
                        segment.subsegments.reduce(
                            (acc, subsegment) => acc + subsegment.value,
                            0,
                        );
                    segment.value = isHidden ? 0 : segmentTotalWithSubsegments;
                    segment.subtitle = `${segmentTotalWithSubsegments} ${pluralize("document", segmentTotalWithSubsegments)}`;
                    segment.valueOverride = `${((segmentTotalWithSubsegments / total) * 100).toFixed(2)}%`;
                }
                return segment;
            })
            .filter((segment) => segment !== undefined);
    }, [total, categoriesTree, categories, statsMap]);

    return (
        <div className={styles.categoryStatsContainer}>
            <SegmentBar
                className={styles.categoryStats}
                segments={segments}
                hasMinimumWidth
                showSubsegmentsOnHover
                onSegmentClick={toggleCategory}
            />
        </div>
    );
};
